import { useState, useEffect, useRef } from "react";
import useTheme from "../components/useTheme";
import { ReactComponent as Logo } from '../assets/kcg.svg';
import { ReactComponent as Logon } from '../assets/kcgn.svg';

const STARS_COUNT = 130;
const PARTICLES_COUNT = 18;

function Stars() {
    const stars = useRef(
        Array.from({ length: STARS_COUNT }, () => ({
            left: Math.random() * 100,
            top: Math.random() * 100,
            size: Math.random() * 2.4 + 0.5,
            delay: Math.random() * 6,
            duration: Math.random() * 2 + 2,
            d: 2 + Math.random() * 4,
            dl: Math.random() * 6,
        }))
    ).current;

    return (
        <div className="stars">
            {stars.map((s, i) => (
                <div
                    key={i}
                    className="star"
                    style={{
                        left: `${s.left}%`,
                        top: `${s.top}%`,
                        width: `${s.size}px`,
                        height: `${s.size}px`,
                        "--d": `${s.d}s`,
                        "--dl": `${s.dl}s`,
                    }}
                />
            ))}
        </div>
    );
}

function Particles() {
    const particles = useRef(
        Array.from({ length: PARTICLES_COUNT }, (_, i) => ({
            id: i,
            size: Math.random() * 6 + 3,
            left: Math.random() * 100,
            bottom: Math.random() * 40,
            duration: Math.random() * 8 + 6,
            delay: Math.random() * 8,
            pd: Math.random() * 8 + 6,
            pdl: Math.random() * 8,
        }))
    ).current;

    return (
        <div className="particles">
            {particles.map((p) => (
                <div
                    key={p.id}
                    className="particle"
                    style={{
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        bottom: `${p.bottom}%`,
                        left: `${p.left}%`,
                        background: "rgba(93,190,106,0.3)",
                        "--pd": `${p.pd}s`,
                        "--pdl": `${p.pdl}s`,
                    }}
                />
            ))}
        </div>
    );
}

function Clouds() {
    const clouds = [
        { top: "6%", w: "190px", h: "55px", cd: "32s", cdl: "0s" },
        { top: "15%", w: "130px", h: "40px", cd: "48s", cdl: "-18s" },
        { top: "4%", w: "220px", h: "65px", cd: "58s", cdl: "-28s" },
        { top: "22%", w: "100px", h: "32px", cd: "38s", cdl: "-8s" },
    ];

    return (
        <div className="cloud-layer">
            {clouds.map((c, i) => (
                <div
                    key={i}
                    className="cloud"
                    style={{
                        top: c.top,
                        width: c.w,
                        height: c.h,
                        "--cd": c.cd,
                        "--cdl": c.cdl,
                        left: "-15%",
                    }}
                />
            ))}
        </div>
    );
}

// Toast types config
const TOAST_TYPES = {
    error: { bg: "#ef4444", icon: "❌" },
    warning: { bg: "#f59e0b", icon: "⚠️" },
    success: { bg: "#22c55e", icon: "✅" },
    info: { bg: "#3b82f6", icon: "ℹ️" },
};

function ToastStack({ toasts }) {
    return (
        <div className="toast-stack">
            {toasts.map((t) => (
                <div key={t.id} className={`toast-item toast-${t.type} ${t.exiting ? "toast-exit" : "toast-enter"}`}>
                    <span className="toast-icon">{TOAST_TYPES[t.type].icon}</span>
                    <span className="toast-msg">{t.msg}</span>
                </div>
            ))}
        </div>
    );
}


export default function Login() {
    const { dark, toggle } = useTheme();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [shake, setShake] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [ripple, setRipple] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({ username: false, password: false });
    const btnRef = useRef(null);
    const toastCounter = useRef(0);

    function pushToast(msg, type = "error", duration = 3200) {
        const id = ++toastCounter.current;
        setToasts((prev) => [...prev, { id, msg, type, exiting: false }]);
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => t.id === id ? { ...t, exiting: true } : t));
            setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 400);
        }, duration);
    }

    function triggerShake() {
        setShake(true);
        setTimeout(() => setShake(false), 400);
    }

    const handleRipple = (e) => {
        const rect = btnRef.current.getBoundingClientRect();
        const sz = Math.max(rect.width, rect.height) * 2;
        setRipple({ 
            x: e.clientX - rect.left - sz / 2, 
            y: e.clientY - rect.top - sz / 2, 
            size: sz 
        });
        setTimeout(() => setRipple(null), 700);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        handleRipple(e);

        setFieldErrors({ username: false, password: false });

        if (!username.trim() || !password) {
            setFieldErrors({
                username: !username.trim(),
                password: !password,
            });
            pushToast("Username and password are required.", "warning");
            triggerShake();
            return;
        }

        setStatus("loading");
        pushToast("Verifying your credentials…", "info", 1800);

        try {
            const res = await fetch("https://kcg-credits-back.onrender.com/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ username: username.trim(), password }),
            });

            const data = await res.json();

            if (!res.ok) {
                if (data.detail && Array.isArray(data.detail)) {
                    data.detail.forEach(err => pushToast(err.msg, "error"));
                } else if (data.detail) {
                    pushToast(data.detail, "error");
                } else {
                    pushToast("Login failed.", "error");
                }
                setStatus("idle");
                triggerShake();
                return;
            }

            // Store auth token and student data in localStorage
            localStorage.setItem("studentInfo", JSON.stringify(data.student_info));
            // Only store semester data if it exists and has content
            if (data.semester_data && Object.keys(data.semester_data).length > 0) {
                localStorage.setItem("semesterData", JSON.stringify(data.semester_data));
            } else {
                // Clear previous semester data if new response has none
                localStorage.removeItem("semesterData");
            }

            setStatus("success");
            pushToast("Login successful! Redirecting…", "success", 2500);
            setTimeout(() => window.location.href = "/dashboard", 2500);
        } catch (err) {
            console.error(err);
            pushToast("Server error. Try again later.", "error");
            setStatus("idle");
        }
    };

    return (
        <div data-theme={dark ? "dark" : "light"} className="login-page">
            <style>{css}</style>
            
            <Stars />
            <Particles />
            <Clouds />

            <button 
                className="theme-btn" 
                onClick={toggle} 
                aria-label="Toggle theme"
                title={dark ? "Light mode" : "Dark mode"}
            >
                {dark ? "🌙" : "🌞"}
            </button>

            <div className={`page ${shake ? "shake" : ""}`}>
                <div className="logo">
                    <div className="arc" />
                    <div className="logo-text">KCG Credits</div>
                    <div className="logo-sub">Student Portal</div>
                </div>

                <div className="card">
                    <p className="welcome-text">Welcome Back!</p>

                    <div className={`field ${fieldErrors.username ? "field-error" : ""}`}>
                        <span className="f-icon">👤</span>
                        <input 
                            type="text" 
                            id="uname" 
                            placeholder="Enter Username / Email" 
                            autoComplete="username"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                if (fieldErrors.username) setFieldErrors((f) => ({ ...f, username: false }));
                            }}
                            style={fieldErrors.username ? { borderColor: "#ef4444", boxShadow: "0 0 0 3.5px rgba(239,68,68,0.15)" } : {}}
                        />
                        {fieldErrors.username && <span className="field-err-ico">⚠️</span>}
                    </div>
                    {fieldErrors.username && (
                        <span className="field-err-msg">Please check your username or email</span>
                    )}

                    <div className={`field ${fieldErrors.password ? "field-error" : ""}`}>
                        <span className="f-icon">🔒</span>
                        <input 
                            type={showPass ? "text" : "password"} 
                            id="pwd" 
                            placeholder="Password" 
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (fieldErrors.password) setFieldErrors((f) => ({ ...f, password: false }));
                            }}
                            style={fieldErrors.password ? { borderColor: "#ef4444", boxShadow: "0 0 0 3.5px rgba(239,68,68,0.15)" } : {}}
                        />
                        <span 
                            className="f-icon-r" 
                            id="eyeBtn"
                            onClick={() => setShowPass((s) => !s)}
                            role="button"
                            tabIndex="0"
                        >
                            {showPass ? "🙈" : "👁️"}
                        </span>
                        {fieldErrors.password && <span className="field-err-ico">⚠️</span>}
                    </div>
                    {fieldErrors.password && (
                        <span className="field-err-msg">Please check your password</span>
                    )}

                    <div className="options-row">
                        <label className="remember-label">
                            <input 
                                type="checkbox" 
                                id="remCb" 
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <span className="chk">{rememberMe ? "✓" : ""}</span>
                            Remember Me
                        </label>
                        <span className="forgot">Forgot Password?</span>
                    </div>

                    <button 
                        className="login-btn" 
                        id="loginBtn"
                        ref={btnRef}
                        onClick={handleLogin}
                        disabled={status === "loading" || status === "success"}
                    >
                        {status === "loading" ? "⏳ Verifying…" : status === "success" ? "✓ Success!" : "Login"}
                        {ripple && (
                            <span
                                className="ripple"
                                style={{
                                    width: `${ripple.size}px`,
                                    height: `${ripple.size}px`,
                                    left: `${ripple.x}px`,
                                    top: `${ripple.y}px`,
                                }}
                            />
                        )}
                    </button>

                    <div className="scene">
                        {dark ? <Logon /> : <Logo />}
                    </div>

                    <p className="bottom-link">Don't have an account? <a href="/signup">Sign Up</a></p>
                </div>

                <ToastStack toasts={toasts} />
            </div>
        </div>
    );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

:root {
  --bg-top: #a8d8f0;
  --bg-bottom: #e4f4ff;
  --card-bg: rgba(255,255,255,0.72);
  --card-border: rgba(255,255,255,0.9);
  --input-bg: rgba(255,255,255,0.82);
  --input-border: rgba(46,125,50,0.22);
  --input-focus: #2e7d32;
  --text-primary: #1a3a1a;
  --text-secondary: #2d7a2d;
  --text-muted: #88aa88;
  --btn-bg: linear-gradient(135deg,#2e7d32,#1b5e20);
  --btn-shadow: rgba(46,125,50,0.3);
  --logo-color: #1a7a1a;
  --logo-arc: #5dbe6a;
  --link-color: #1a7a1a;
  --shadow: 0 12px 48px rgba(30,100,30,0.14);
  --toggle-bg: rgba(255,255,255,0.65);
  --toggle-border: rgba(255,255,255,0.9);
  --star-opacity: 0;
  --ground: #5cb86a;
  --water: #5bb8e8;
}

[data-theme="dark"] {
  --bg-top: #060c18;
  --bg-bottom: #111827;
  --card-bg: rgba(17,24,39,0.82);
  --card-border: rgba(255,255,255,0.07);
  --input-bg: rgba(15,23,42,0.75);
  --input-border: rgba(148,163,184,0.15);
  --input-focus: #818cf8;
  --text-primary: #e2e8f0;
  --text-secondary: #94a3b8;
  --text-muted: #475569;
  --btn-bg: linear-gradient(135deg,#4f46e5,#4338ca);
  --btn-shadow: rgba(79,70,229,0.4);
  --logo-color: #93c5fd;
  --logo-arc: #818cf8;
  --link-color: #818cf8;
  --shadow: 0 12px 48px rgba(0,0,0,0.55);
  --toggle-bg: rgba(255,255,255,0.05);
  --toggle-border: rgba(255,255,255,0.1);
  --star-opacity: 1;
  --ground: #162416;
  --water: #0a1530;
}

*,*::before,*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.login-page {
  font-family: 'Nunito', sans-serif;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--bg-top) 0%, var(--bg-bottom) 65%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  transition: background 0.7s ease;
}

/* Stars */
.stars {
  position: fixed;
  inset: 0;
  pointer-events: none;
  opacity: var(--star-opacity);
  transition: opacity 0.7s;
  z-index: 1;
}

.star {
  position: absolute;
  background: #fff;
  border-radius: 50%;
  animation: twinkle var(--d, 3s) ease-in-out infinite;
  animation-delay: var(--dl, 0s);
}

@keyframes twinkle {
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.5); }
}

/* Particles */
.particles {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  border-radius: 50%;
  animation: float var(--pd, 8s) ease-in-out infinite;
  animation-delay: var(--pdl, 0s);
  opacity: 0.4;
}

@keyframes float {
  0%, 100% { transform: translateY(0) translateX(0); opacity: 0; }
  20%, 80% { opacity: 0.4; }
  50% { transform: translateY(-80px) translateX(30px); opacity: 0.6; }
}

/* Clouds */
.cloud-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  transition: opacity 0.7s;
}

[data-theme="dark"] .cloud-layer {
  opacity: 0.04;
}

.cloud {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50px;
  animation: drift var(--cd, 30s) linear infinite;
  animation-delay: var(--cdl, 0s);
}

.cloud::before,
.cloud::after {
  content: '';
  position: absolute;
  background: inherit;
  border-radius: 50%;
}

.cloud::before {
  width: 60%;
  height: 200%;
  top: -80%;
  left: 15%;
}

.cloud::after {
  width: 40%;
  height: 160%;
  top: -50%;
  right: 10%;
}

@keyframes drift {
  from { transform: translateX(-150px); }
  to { transform: translateX(110vw); }
}

/* Theme toggle */
.theme-btn {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 200;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1.5px solid var(--toggle-border);
  background: var(--toggle-bg);
  backdrop-filter: blur(12px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.theme-btn:hover {
  transform: scale(1.12) rotate(20deg);
}

/* Page layout */
.page {
  width: 100%;
  max-width: 430px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 20px 20px;
  position: relative;
  z-index: 10;
}

/* Logo */
.logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-40px) scale(0.9); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.arc {
  display: inline-block;
  width: 90px;
  height: 16px;
  border-top: 5px solid var(--logo-arc);
  border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  margin-bottom: -2px;
  transition: border-color 0.7s;
}



.logo-text {
  font-size: clamp(34px, 8vw, 48px);
  font-weight: 900;
  color: var(--logo-color);
  letter-spacing: -1.5px;
  line-height: 1;
  transition: color 0.7s;
  text-shadow: 0 2px 12px rgba(30, 107, 53, 0.12);
}



.logo-sub {
  font-size: 0.55 rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--text-muted);
  text-transform: uppercase;
  margin-top: 4px;
  transition: color 0.7s;
}

/* Card */
.card {
  width: 100%;
  background: var(--card-bg);
  border: 1.5px solid var(--card-border);
  border-radius: 28px;
  padding: clamp(28px, 5vw, 38px) clamp(24px, 5vw, 36px);
  backdrop-filter: blur(24px);
  box-shadow: var(--shadow);
  transition: all 0.7s;
  animation: slideUp 0.8s 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.welcome-text {
  font-size: clamp(20px, 5vw, 26px);
  font-weight: 900;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 26px;
  transition: color 0.7s;
}

/* Fields */
.field {
  position: relative;
  margin-bottom: 14px;
}

.f-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 17px;
  pointer-events: none;
  transition: color 0.3s;
}

.f-icon-r {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 17px;
  cursor: pointer;
  transition: all 0.3s;
  user-select: none;
}

.f-icon-r:hover {
  transform: translateY(-50%) scale(1.15);
}

.field input {
  width: 100%;
  height: 52px;
  background: var(--input-bg);
  border: 1.5px solid var(--input-border);
  border-radius: 14px;
  padding: 0 46px;
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  outline: none;
  transition: all 0.3s;
}

.field input::placeholder {
  color: var(--text-muted);
  font-weight: 600;
}

.field input:focus {
  border-color: var(--input-focus);
  box-shadow: 0 0 0 3.5px rgba(45, 138, 78, 0.13);
  background: var(--input-bg);
}

[data-theme="dark"] .field input:focus {
  box-shadow: 0 0 0 3.5px rgba(107, 191, 138, 0.1);
}

.field-error input {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3.5px rgba(239, 68, 68, 0.15) !important;
}

.field-err-msg {
  font-size: 12px;
  font-weight: 800;
  color: #ef4444;
  margin-top: 4px;
  display: block;
  padding-left: 2px;
  animation: slideDown 0.3s ease both;
}

.field-err-ico {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  pointer-events: none;
}

/* Options row */
.options-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0 20px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-secondary);
  transition: color 0.7s;
  user-select: none;
}

.remember-label input {
  display: none;
}

.chk {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 2px solid var(--input-border);
  background: var(--input-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.3s;
  color: #fff;
}

.remember-label input:checked + .chk {
  background: var(--input-focus);
  border-color: var(--input-focus);
  color: #fff;
}

.forgot {
  font-size: 14px;
  font-weight: 700;
  color: var(--link-color);
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.3s;
}

.forgot:hover {
  opacity: 0.7;
}

/* Button */
.login-btn {
  width: 100%;
  height: 54px;
  background: var(--btn-bg);
  border: none;
  border-radius: 16px;
  color: #fff;
  font-family: 'Nunito', sans-serif;
  font-size: 17px;
  font-weight: 900;
  letter-spacing: 0.5px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 8px 24px var(--btn-shadow);
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px var(--btn-shadow);
}

.login-btn:active:not(:disabled) {
  transform: translateY(-1px);
}

.login-btn:disabled {
  opacity: 0.8;
  cursor: default;
}

.login-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, transparent 55%);
  pointer-events: none;
}

.ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  animation: rip 0.65s linear;
  pointer-events: none;
}

@keyframes rip {
  from {
    width: 0;
    height: 0;
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  to {
    width: 250px;
    height: 250px;
    opacity: 0;
    transform: translate(-50%, -50%);
  }
}

/* Scene */
.scene {
  width: 100%;
  max-width: 480px;
  position: relative;
  animation: slideUp 0.8s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  margin-top: -8px;
}

/* Bottom link */
.bottom-link {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-secondary);
  animation: slideUp 0.8s 0.4s ease both;
  transition: color 0.7s;
}

.bottom-link a {
  color: var(--link-color);
  font-weight: 900;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.3s;
}

.bottom-link a:hover {
  opacity: 0.75;
}

/* Toast stack */
.toast-stack {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 999;
  pointer-events: none;
  width: max-content;
  max-width: 90vw;
}

.toast-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px 12px 16px;
  border-radius: 50px;
  color: #fff;
  font-weight: 800;
  font-size: 0.92rem;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(8px);
  pointer-events: auto;
  min-width: 220px;
  justify-content: center;
}

.toast-enter {
  animation: toastIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.toast-exit {
  animation: toastOut 0.35s ease forwards;
}

.toast-error {
  background: #ef4444;
}

.toast-warning {
  background: #f59e0b;
}

.toast-success {
  background: #22c55e;
}

.toast-info {
  background: #3b82f6;
}

.toast-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.toast-msg {
  line-height: 1.3;
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes toastOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(16px) scale(0.9);
  }
}

/* Shake animation */
.shake {
  animation: shake 0.4s ease;
}

@keyframes shake {
  0%, 100% { transform: none; }
  20%, 60% { transform: translateX(-7px); }
  40%, 80% { transform: translateX(7px); }
}

/* Responsive */
@media (max-width: 480px) {
  .page {
    padding: 28px 14px 16px;
  }
  .theme-btn {
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  .card {
    padding: 24px 18px 20px;
  }
}

@media (min-width: 700px) {
  .login-page {
    padding: 20px;
  }
  .page {
    min-height: auto;
    gap: 18px;
  }
}
`;